import { DownloadOutlined } from '@ant-design/icons';
import { Button, Card, DatePicker, Form, Select, Typography, message } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { downloadFile, errorMessage } from '../api/client';
import { useInvestors } from '../api/hooks';
import { apiDate } from '../utils/format';

type ReportType = 'investor-statement' | 'profit' | 'outstanding-profit' | 'daily' | 'monthly' | 'yearly';

const REPORTS: { value: ReportType; label: string }[] = [
  { value: 'investor-statement', label: 'Investor statement' },
  { value: 'profit', label: 'Profit report' },
  { value: 'outstanding-profit', label: 'Outstanding profit' },
  { value: 'daily', label: 'Daily report' },
  { value: 'monthly', label: 'Monthly report' },
  { value: 'yearly', label: 'Yearly report' },
];

interface FormValues {
  type: ReportType;
  format: 'pdf' | 'excel';
  investorId?: number;
  range?: [dayjs.Dayjs, dayjs.Dayjs];
  day?: dayjs.Dayjs;
  month?: dayjs.Dayjs;
  year?: dayjs.Dayjs;
}

export default function ReportsPage() {
  const [form] = Form.useForm<FormValues>();
  const [type, setType] = useState<ReportType>('investor-statement');
  const [downloading, setDownloading] = useState(false);

  // Investor picker only needs the first page of names; search narrows it server-side.
  const [search, setSearch] = useState<string>();
  const { data: investors } = useInvestors({ page: 1, pageSize: 50, search });

  const onFinish = async (values: FormValues) => {
    setDownloading(true);
    try {
      const { url, name } = buildRequest(values);
      await downloadFile(url, name);
    } catch (err) {
      message.error(errorMessage(err));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <Typography.Title level={3}>Reports</Typography.Title>

      <Card style={{ maxWidth: 640 }}>
        <Form<FormValues>
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            type: 'investor-statement',
            format: 'pdf',
            range: [dayjs().startOf('month'), dayjs()],
            day: dayjs(),
            month: dayjs(),
            year: dayjs(),
          }}
        >
          <Form.Item name="type" label="Report type" rules={[{ required: true }]}>
            <Select options={REPORTS} onChange={setType} />
          </Form.Item>

          {type === 'investor-statement' && (
            <Form.Item name="investorId" label="Investor" rules={[{ required: true, message: 'Select an investor.' }]}>
              <Select
                showSearch
                filterOption={false}
                onSearch={setSearch}
                placeholder="Search by name or code"
                options={(investors?.items ?? []).map((i) => ({
                  value: i.id,
                  label: `${i.investorCode} — ${i.fullName}`,
                }))}
              />
            </Form.Item>
          )}

          {(type === 'investor-statement' || type === 'profit') && (
            <Form.Item name="range" label="Date range" rules={[{ required: true }]}>
              <DatePicker.RangePicker style={{ width: '100%' }} />
            </Form.Item>
          )}

          {type === 'daily' && (
            <Form.Item name="day" label="Date" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          )}

          {type === 'monthly' && (
            <Form.Item name="month" label="Month" rules={[{ required: true }]}>
              <DatePicker picker="month" style={{ width: '100%' }} />
            </Form.Item>
          )}

          {type === 'yearly' && (
            <Form.Item name="year" label="Year" rules={[{ required: true }]}>
              <DatePicker picker="year" style={{ width: '100%' }} />
            </Form.Item>
          )}

          <Form.Item name="format" label="Format" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'pdf', label: 'PDF' },
                { value: 'excel', label: 'Excel (.xlsx)' },
              ]}
            />
          </Form.Item>

          <Button type="primary" htmlType="submit" icon={<DownloadOutlined />} loading={downloading} block>
            Download
          </Button>
        </Form>
      </Card>
    </>
  );
}

function buildRequest(values: FormValues): { url: string; name: string } {
  const { type, format } = values;
  const ext = format === 'excel' ? 'xlsx' : 'pdf';
  const from = apiDate(values.range?.[0]);
  const to = apiDate(values.range?.[1]);

  switch (type) {
    case 'investor-statement':
      return {
        url: `/reports/investor-statement/${values.investorId}?from=${from}&to=${to}&format=${format}`,
        name: `statement.${ext}`,
      };
    case 'profit':
      return { url: `/reports/profit?from=${from}&to=${to}&format=${format}`, name: `profit_report.${ext}` };
    case 'outstanding-profit':
      return { url: `/reports/outstanding-profit?format=${format}`, name: `outstanding_profit.${ext}` };
    case 'daily':
      return {
        url: `/reports/daily?date=${apiDate(values.day)}&format=${format}`,
        name: `daily_report.${ext}`,
      };
    case 'monthly':
      return {
        url: `/reports/monthly?year=${values.month?.year()}&month=${(values.month?.month() ?? 0) + 1}&format=${format}`,
        name: `monthly_report.${ext}`,
      };
    case 'yearly':
      return {
        url: `/reports/yearly?year=${values.year?.year()}&format=${format}`,
        name: `yearly_report.${ext}`,
      };
  }
}
